/* eslint-disable camelcase */
import Vue from "vue";
import { GetterTree, ActionTree, MutationTree } from "vuex";
import { Asset, Address } from "@foxone/api/src/models/wallet/mixin";
import utils from "@/utils";

const state = () => ({
  assets: [],
  addedAssets: [],
  addressesMap: {},
  hidden: false
});

export type AssetsState = ReturnType<
  () => {
    assets: Asset[];
    addedAssets: Asset[];
    addressesMap: {
      [x: string]: Address[];
    };
    hidden: boolean;
  }
>;

const getters: GetterTree<AssetsState, {}> = {
  getMergedAssets({ assets, addedAssets }) {
    return [...assets, ...addedAssets];
  },
  getTotalUsd({ assets }) {
    if (!Array.isArray(assets)) {
      return 0;
    }
    const total = assets.reduce((acc, asset) => {
      const { price_usd, balance } = asset;
      return Number(price_usd) * Number(balance) + acc;
    }, 0);
    return utils.number.toPrecision(total, 2);
  },
  getTotalUsdtById({ assets, addedAssets }) {
    return id => {
      const asset = [...assets, ...addedAssets].find(a => a.asset_id === id);
      if (asset) {
        const v = Number(asset.balance) * Number(asset.price_usd);
        return utils.number.toPrecision(v, 2);
      }
      return 0;
    };
  },
  getAssetById({ assets, addedAssets }) {
    return id => {
      return [...assets, ...addedAssets].find(a => a.asset_id === id);
    };
  }
};

const mutations: MutationTree<AssetsState> = {
  SET_ASSETS(state, assets) {
    state.assets = assets;
  },
  REMOVE_ASSETS(state) {
    state.assets = [];
  },
  SET_ADDRESSES(state, { addresses, assetId }) {
    Vue.set(state.addressesMap, assetId, addresses);
  },
  REMOVE_ADDRESSES(state) {
    Vue.set(state, "addressesMap", {});
  },
  SET_HIDDEN(state, value) {
    state.hidden = value;
  },
  ADD_ADDED_ASSET({ addedAssets }, asset) {
    const added = addedAssets.find(x => x.asset_id === asset.asset_id);
    if (!added) {
      addedAssets.push(asset);
    }
  },
  REMOVE_ADDED_ASSET({ addedAssets }, asset) {
    const index = addedAssets.findIndex(x => x.asset_id === asset.asset_id);
    if (index > -1) {
      addedAssets.splice(index);
    }
  },
  REMOVE_ALL_ADDED_ASSET(state) {
    state.addedAssets = [];
  }
};

const actions: ActionTree<AssetsState, {}> = {
  async loadAssets({ commit, state }) {
    try {
      const res = await this.$api.wallet.mixin.getAssets();
      commit("SET_ASSETS", res || []);
      for (const asset of res) {
        const added = state.addedAssets.find(
          x => x.asset_id === asset.asset_id
        );
        if (added) {
          commit("REMOVE_ADDED_ASSET", asset);
        }
      }
    } catch (error) {
      console.log(error);
    }
  },
  async loadAddresses({ commit }, assetId) {
    try {
      const res = await this.$api.wallet.mixin.getAssetAddresses(assetId);
      commit("SET_ADDRESSES", { addresses: res, assetId });
    } catch (error) {
      console.log(error);
    }
  },
  async removeAssets({ commit }) {
    commit("SET_ASSETS", []);
    commit("REMOVE_ALL_ADDED_ASSET");
    commit("REMOVE_ADDRESSES");
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
